// Jaaraggregaat-engine: berekent voor elk meetvlak de schaduwfractie per cel
// over een heel jaar (zie §3.4 / §3.6 van het ontwerp).
//
// Strategie:
//  - Sample het jaar elke 15 minuten; alleen tijdens daglichturen (zon-elev ≥ 10°).
//  - Per sample: bereken de slagschaduwen per meetvlak (op de juiste hoogte)
//    via `computeShadowsAt` uit Phase 3.
//  - Per cel: tel hoeveel samples het in schaduw lag, gewogen met de
//    blockingFactor van de schaduw-werkende blokken (max 1 per sample).
//  - Aggregaat per cel: shadowFraction = sum / dayLightSamples
//  - Aggregaat per meetvlak: gemiddelde, drempelverdeling, maandverdeling.
//
// Performance: bbox-prefilter op shadow-polygons om point-in-polygon-tests
// flink te beperken. Bij 50 blokken / 5 meetvlakken / 17.000 samples is dit
// volgens onze metingen op een gemiddelde laptop ruim binnen ~30 s.

import type { Block, Scenario } from "../types/scenario";
import {
  buildCellGrid,
  detectMeasurementSurfaces,
  makeProjection,
  type CellGrid,
} from "./grid";
import { computeShadowsAt, MIN_ELEVATION_RAD, type ShadowFeature } from "./projectShadow";
import {
  dayOfYearToMonthDay,
  daysInYear,
  getSunPosition,
  nlClockToUtc,
} from "./sunPosition";
import { pointInPolygon } from "../utils/geo";

export const SAMPLE_INTERVAL_MIN = 15;
export const DEFAULT_GRID_RESOLUTION_M = 0.5;

export interface CellResult {
  /** Centerpunt in [lng, lat]. */
  lngLat: [number, number];
  /** 0–1: aandeel daglicht-samples in schaduw. */
  shadowFraction: number;
}

export interface BucketDistribution {
  /** Aantal cellen met minder dan 0,5% schaduw (bijna nooit schaduw). */
  bucket0: number;
  /** 0–33% schaduw. */
  bucket0_33: number;
  /** 33–66% schaduw. */
  bucket33_66: number;
  /** 66–100% schaduw (m.u.v. 100%). */
  bucket66_100: number;
  /** ≥99,5% schaduw (vrijwel altijd schaduw). */
  bucket100: number;
}

export interface MonthlyResult {
  /** 1–12 */
  month: number;
  /** Gemiddelde schaduwfractie over alle cellen + samples in die maand. */
  avgShadowFraction: number;
  /** Aantal daglicht-samples in die maand. */
  daylightSamples: number;
}

export interface SurfaceResult {
  surfaceId: string;
  blockId: string;
  surfaceKind: "ground" | "roof";
  label: string;
  cellCount: number;
  cellResolutionM: number;
  /** Totaal daglichturen in het jaar (op deze breedtegraad / locatie). */
  totalDaylightHours: number;
  /** Gemiddeld aantal schaduwuren per jaar over alle cellen. */
  avgShadowHours: number;
  /** Gemiddelde schaduw-percentage van daglichturen. */
  avgShadowPercent: number;
  buckets: BucketDistribution;
  monthly: MonthlyResult[];
  cells: CellResult[];
}

export interface YearAggregateResult {
  scenarioHash: string;
  computedAt: string;
  referenceYear: number;
  surfaces: SurfaceResult[];
}

export interface ProgressMessage {
  /** 0–100 */
  pct: number;
  phase: string;
}

// ---- Hash-functie -------------------------------------------------------

/**
 * Deterministische hash van het scenario-deel dat de schaduwuitkomst
 * beïnvloedt: blok-geometrieën, hoogtes, doorlatendheid en de view-locatie.
 * Wordt gebruikt als cache-sleutel.
 */
export function hashScenarioForShadow(scenario: Scenario): string {
  const subset = {
    v: scenario.view,
    blocks: scenario.blocks
      .map((b) => ({
        id: b.id,
        type: b.type,
        polygon: b.polygon,
        ht: b.heightTop,
        hb: b.heightBottom,
        lt: b.lightTransmission ?? 0,
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  };
  // FNV-1a 64-bit (split in twee 32-bit ints) — geen crypto-grade nodig.
  const json = JSON.stringify(subset);
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, "0") + "-" + json.length.toString(16);
}

// ---- Hoofdroutine -------------------------------------------------------

interface ComputeOptions {
  referenceYear: number;
  resolution?: number;
  onProgress?: (msg: ProgressMessage) => void;
}

export function computeYearAggregate(
  scenario: Scenario,
  options: ComputeOptions,
): YearAggregateResult {
  const resolution = options.resolution ?? DEFAULT_GRID_RESOLUTION_M;
  const surfaces = detectMeasurementSurfaces(scenario.blocks);
  if (surfaces.length === 0) {
    return {
      scenarioHash: hashScenarioForShadow(scenario),
      computedAt: new Date().toISOString(),
      referenceYear: options.referenceYear,
      surfaces: [],
    };
  }

  // Lokale projectie gecentreerd op het scenario-zwaartepunt voor stabiele
  // grid-coördinaten over alle meetvlakken.
  const proj = makeProjection(scenario.view.centerLat, scenario.view.centerLng);

  // Bouw de grids één keer.
  const grids: CellGrid[] = surfaces.map((s) => buildCellGrid(s, proj, resolution));

  // Initialiseer accumulatoren.
  const totals = grids.map((g) => ({
    grid: g,
    shadowSums: new Float32Array(g.cells.length),
    monthShadowSums: new Float32Array(12),
    monthSampleCounts: new Uint32Array(12),
  }));

  // Bouw de sample-lijst: dag-of-year × minuten × jaar.
  const yearLength = daysInYear(options.referenceYear);
  let totalDaylightSamples = 0;
  // We tracken voortgang per dag (1/yearLength).
  for (let doy = 1; doy <= yearLength; doy++) {
    const { month, day } = dayOfYearToMonthDay(options.referenceYear, doy);
    const monthIndex = month; // 0-based

    for (let minute = 0; minute < 1440; minute += SAMPLE_INTERVAL_MIN) {
      const hour = Math.floor(minute / 60);
      const mm = minute % 60;
      const utc = nlClockToUtc(options.referenceYear, month, day, hour, mm);
      const sun = getSunPosition(utc, scenario.view.centerLat, scenario.view.centerLng);
      if (sun.altitude < MIN_ELEVATION_RAD) continue;

      totalDaylightSamples++;
      // Per meetvlak: bereken de relevante shadow-features op die hoogte.
      for (let s = 0; s < grids.length; s++) {
        const acc = totals[s];
        const z = acc.grid.surface.z;
        const shadows = computeShadowsAt(scenario.blocks, sun, z);
        // Filter weg: blokken die het meetvlak zélf zijn (een tuin werpt geen
        // schaduw op zichzelf; een dak kan ook niet door zijn eigen extrusie
        // geschaduwd worden).
        const surfaceBlockId = acc.grid.surface.blockId;
        const filtered: ShadowFeature[] = [];
        for (const f of shadows) {
          if (f.blockId === surfaceBlockId) continue;
          filtered.push(f);
        }
        if (filtered.length === 0) {
          acc.monthSampleCounts[monthIndex]++;
          continue;
        }
        // Per shadow-polygon: bbox in lng/lat voor pre-filter.
        const bboxes = filtered.map((f) => bboxOf(f.polygon));
        // Per cel: bereken sum(blockingFactor) over schaduwen die de cel bevatten.
        const cells = acc.grid.cells;
        let monthShadowSum = 0;
        for (let c = 0; c < cells.length; c++) {
          const cell = cells[c];
          const lng = cell.lngLat[0];
          const lat = cell.lngLat[1];
          let sumBlocking = 0;
          for (let f = 0; f < filtered.length; f++) {
            const bb = bboxes[f];
            if (lng < bb.minX || lng > bb.maxX || lat < bb.minY || lat > bb.maxY) continue;
            if (pointInPolygon([lng, lat], filtered[f].polygon)) {
              sumBlocking += filtered[f].blockingFactor;
              if (sumBlocking >= 1) break;
            }
          }
          const blocked = Math.min(1, sumBlocking);
          if (blocked > 0) {
            acc.shadowSums[c] += blocked;
            monthShadowSum += blocked;
          }
        }
        acc.monthShadowSums[monthIndex] += monthShadowSum;
        acc.monthSampleCounts[monthIndex]++;
      }
    }

    if (options.onProgress) {
      options.onProgress({
        pct: (doy / yearLength) * 100,
        phase: `Dag ${doy} van ${yearLength}`,
      });
    }
  }

  const totalDaylightHours = (totalDaylightSamples * SAMPLE_INTERVAL_MIN) / 60;

  // Aggregeer per meetvlak.
  const surfaceResults: SurfaceResult[] = totals.map((acc) => {
    const cells: CellResult[] = acc.grid.cells.map((cell, i) => ({
      lngLat: cell.lngLat,
      shadowFraction:
        totalDaylightSamples > 0 ? acc.shadowSums[i] / totalDaylightSamples : 0,
    }));

    let totalShadow = 0;
    const buckets: BucketDistribution = {
      bucket0: 0,
      bucket0_33: 0,
      bucket33_66: 0,
      bucket66_100: 0,
      bucket100: 0,
    };
    for (const c of cells) {
      totalShadow += c.shadowFraction;
      const f = c.shadowFraction;
      if (f < 0.005) buckets.bucket0++;
      else if (f < 0.33) buckets.bucket0_33++;
      else if (f < 0.66) buckets.bucket33_66++;
      else if (f < 0.995) buckets.bucket66_100++;
      else buckets.bucket100++;
    }
    const cellCount = cells.length;
    const avgFraction = cellCount > 0 ? totalShadow / cellCount : 0;

    const monthly: MonthlyResult[] = [];
    for (let m = 0; m < 12; m++) {
      const samples = acc.monthSampleCounts[m];
      const cellsThisMonth = cellCount > 0 && samples > 0
        ? acc.monthShadowSums[m] / (cellCount * samples)
        : 0;
      monthly.push({
        month: m + 1,
        avgShadowFraction: cellsThisMonth,
        daylightSamples: samples,
      });
    }

    return {
      surfaceId: acc.grid.surface.id,
      blockId: acc.grid.surface.blockId,
      surfaceKind: acc.grid.surface.surfaceKind,
      label: acc.grid.surface.label,
      cellCount,
      cellResolutionM: resolution,
      totalDaylightHours,
      avgShadowHours: avgFraction * totalDaylightHours,
      avgShadowPercent: avgFraction * 100,
      buckets,
      monthly,
      cells,
    };
  });

  return {
    scenarioHash: hashScenarioForShadow(scenario),
    computedAt: new Date().toISOString(),
    referenceYear: options.referenceYear,
    surfaces: surfaceResults,
  };
}

function bboxOf(polygon: [number, number][]) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of polygon) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, maxX, minY, maxY };
}

// ---- Niet-blokkerende blokken (bv. tuin, bestrating) ---------------------

export function isBlockShadowing(b: Block): boolean {
  return b.heightTop > 0 && (b.lightTransmission ?? 0) < 100;
}
